import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField
} from '@mui/material';
import { useNotify } from 'react-admin';
import { API_BASE_URL, authFetch } from '../../apiClient.ts';
import { getErrorMessage, parseEmailList } from '../../utils/helperUtils.ts';

type ShareContactsDialogProps = {
  open: boolean;
  onClose: () => void;
  heading: string;
  filter: Record<string, unknown>;
  ids?: number[];
  defaultSubject: string;
  defaultMessage: string;
  successMessage: string;
};

export const ShareContactsDialog: React.FC<ShareContactsDialogProps> = ({
  open,
  onClose,
  heading,
  filter,
  ids,
  defaultSubject,
  defaultMessage,
  successMessage
}) => {
  const notify = useNotify();
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setMessage(defaultMessage);
    }
  }, [open, defaultSubject, defaultMessage]);

  const handleShare = async () => {
    const { emails, invalid } = parseEmailList(to);
    if (invalid.length > 0) {
      notify(`Not a valid email: ${invalid.join(', ')}`, { type: 'warning' });
      return;
    }
    if (emails.length === 0) {
      notify('Enter at least one recipient', { type: 'warning' });
      return;
    }

    setSending(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/contacts/share-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filter,
          ids,
          emails,
          title: subject,
          body: message
        })
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      notify(successMessage, { type: 'success' });
      onClose();
      setTo('');
    } catch (error) {
      notify(getErrorMessage(error), { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{heading}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="To"
            placeholder="alice@example.com, bob@example.com"
            fullWidth
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <TextField
            label="Subject"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleShare} disabled={sending}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};
